import React from "react";
import { reserveLabel, lineContingencyApplies } from "../lib/contingency";
import type { Theme } from "../lib/theme";
import type { Division, ItemState } from "../types";
import type { FinalTotals } from "../lib/calculations";
import { walkItems, getItemTotal, isClarification, calcItemCont, computeMarkupableSubtotal } from "../lib/calculations";
import * as Icon from "../components/OEIcons";

const FONT = "'Inter','Avenir Next','Century Gothic',sans-serif";

interface SummaryTabProps {
  T: Theme;
  isMobile: boolean;
  isTablet: boolean;
  divisions: Division[];
  itemState: Record<string, ItemState>;
  proposals: any[];
  proposalAreas: any;
  final: FinalTotals;
  overhead: number;
  profit: number;
  contingency: number;
  markupMode: string;
  buildingArea: number;
  duration: number;
  projectName: string;
  address: string;
  grandTotal: any;
  divTotals: any;
  contingencyByProp: any;
  contingencyPolicy: import("../lib/contingency").ContingencyPolicy;
  /** Go to a settings tab, remembering to offer the way back here. */
  onEditSetting: (tab: string) => void;
  altTotals: any;
  drawingList: any[];
  orgExclusions: any[];
  budgetRange: {low: number; high: number; name: string} | null;
  projectExclusions: string[];
  setProjectExclusions: React.Dispatch<React.SetStateAction<string[]>>;
  newExclusionText: string;
  setNewExclusionText: React.Dispatch<React.SetStateAction<string>>;
  contingencyDisplay: string;
  setContingencyDisplay: React.Dispatch<React.SetStateAction<any>>;
  showAllowancePanel: boolean;
  setShowAllowancePanel: React.Dispatch<React.SetStateAction<boolean>>;
  showBudgetPicker: boolean;
  setShowBudgetPicker: React.Dispatch<React.SetStateAction<boolean>>;
  bdCollapsed: Record<string, boolean>;
  setBdCollapsed: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  setActiveTab: (tab: string) => void;
  setActiveDiv: (div: string) => void;
  setPrintInitialSheets: React.Dispatch<React.SetStateAction<any>>;
  setShowPrintDialog: React.Dispatch<React.SetStateAction<boolean>>;
  fmt: (n: number) => string;
  fmtDec: (n: number) => string;
  fmtSF: (n: number) => string;
  costSFCents: boolean;
  getPC: (propId: string, proposals: any[]) => any;
  navigateToItem: (itemId: string) => void;
  subTotals: Record<string, number>;
}

/**
 * A figure on the summary that can be changed, and says so.
 *
 * Duration and building area are read here but set on Project Info, and
 * nothing on this page said which page that was -- so the honest options were
 * to hunt the tabs or to give up. A number you can act on should take you to
 * where you act on it.
 */
function EditableStat({ T, onClick, title, children }: {
  T: Theme; onClick: () => void; title: string; children: React.ReactNode;
}) {
  const [hover, setHover] = React.useState(false);
  return (
    <span role="button" tabIndex={0} title={title}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        cursor: "pointer",
        color: hover ? T.borderAccent : "inherit",
        textDecoration: "underline",
        textDecorationStyle: "dotted",
        textUnderlineOffset: 3,
        textDecorationColor: hover ? T.borderAccent : T.textWeak,
        outline: "none",
      }}>
      {children}
    </span>
  );
}

export default function SummaryTab({
  T, isMobile, isTablet,
  divisions, itemState, proposals, proposalAreas,
  final, overhead, profit, contingency, markupMode,
  buildingArea, duration, projectName, address,
  grandTotal, divTotals, contingencyByProp, contingencyPolicy, onEditSetting, altTotals,
  drawingList, orgExclusions, budgetRange,
  projectExclusions, setProjectExclusions,
  newExclusionText, setNewExclusionText,
  contingencyDisplay, setContingencyDisplay,
  showAllowancePanel, setShowAllowancePanel,
  showBudgetPicker, setShowBudgetPicker,
  bdCollapsed, setBdCollapsed,
  setActiveTab, setActiveDiv,
  setPrintInitialSheets, setShowPrintDialog,
  fmt, fmtDec, fmtSF, costSFCents,
  getPC, navigateToItem, subTotals,
}: SummaryTabProps) {
const sumPropAreas = proposals.reduce((a, p) => a + (proposalAreas[p.id] || 0), 0);
const blendedSF = sumPropAreas > 0 ? sumPropAreas : (buildingArea || 0);
const sfFor = (p: string) => (proposalAreas[p] > 0 ? proposalAreas[p] : buildingArea);
const markupableSubtotal = computeMarkupableSubtotal(divisions, itemState);

// Splitting contingency out of the columns only means something when the
// columns carry any. Under a project reserve no division holds a share of it,
// so the split would be offered on a column of dashes and the toggle would ask
// a question with one answer. Both disappear instead.
const contPerLine = lineContingencyApplies(contingencyPolicy);
const contSep = contingencyDisplay === "separate" && contPerLine;

/**
 * Open the whole tree to one level.
 *
 * The collapse map mixes two conventions -- a package key is truthy when
 * OPEN, a division or subcategory key is truthy when CLOSED -- so this
 * writes both rather than toggling. It also writes every key rather than
 * merging into what is there, which is what makes the result the same
 * whatever state the tree was in when you pressed it.
 */
const setTreeDepth = (depth: 0 | 1 | 2 | 3) => {
  const next: Record<string, boolean> = {};
  for (const prop of proposals) {
    next[`pkg_${prop.id}`] = depth >= 1;
    for (const div of divisions) {
      next[`${prop.id}_${div.id}`] = depth < 2;
      for (const sub of (div.subcategories || [])) {
        next[`sub_${prop.id}_${div.id}_${sub.id}`] = depth < 3;
      }
    }
  }
  setBdCollapsed(next);
};

return (
<div className="fade-in" style={{padding:isMobile?"14px 16px":isTablet?"20px 24px":"28px 36px",maxWidth:1100}}>
  {/* Construction phase banner */}
  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:4}}>
    <div style={{fontSize:24,fontWeight:700,color:T.borderAccent}}>Cost Summary</div>
    <div style={{display:"flex",alignItems:"center",gap:6}}>
      <button onClick={()=>{setPrintInitialSheets(["summary"]);setShowPrintDialog(true);}}
        style={{fontSize:10,padding:"4px 12px",minHeight:24,borderRadius:3,border:`1px solid ${T.borderAccent}55`,cursor:"pointer",fontFamily:FONT,fontWeight:600,
          background:T.bgAccentLight,color:T.borderAccent}}>
        Export PDF
      </button>
    </div>
  </div>
  <div style={{fontSize:11,color:T.textWeak,marginBottom:22,display:"flex",alignItems:"center",gap:12,flexWrap:"wrap" as any}}>
    <span>{projectName}{address?` · ${address}`:""} · <EditableStat T={T} onClick={()=>onEditSetting("properties")} title="Set the building area on Project Info">{buildingArea.toLocaleString()} SF</EditableStat> · <EditableStat T={T} onClick={()=>onEditSetting("properties")} title="Set the duration on Project Info">{duration} Month{duration!==1?"s":""}</EditableStat></span>
    {drawingList.length>0&&(()=>{
      const latestDate=drawingList.filter(d=>d.date).map(d=>d.date).sort().pop();
      return latestDate?(
        <span style={{color:T.textMuted}}>· Drawing Set: <span style={{color:T.textSecondary,fontWeight:600}}>{latestDate}</span></span>
      ):null;
    })()}
  </div>
  {/* ── Onboarding card (shown only when estimate is empty) ── */}
  {final.total===0&&grandTotal.subtotal===0&&(
    <div style={{
      background:T.bgPanel,border:`1px solid ${T.borderAccent}44`,
      borderRadius:10,padding:"28px 32px",marginBottom:24,
      display:"flex",gap:28,flexWrap:"wrap" as any,
    }}>
      <div style={{flex:1,minWidth:220}}>
        <div style={{fontSize:16,fontWeight:700,color:T.borderAccent,marginBottom:6,fontFamily:FONT}}>Welcome to OpenEstimate</div>
        <div style={{fontSize:13,color:T.textMuted,lineHeight:1.7,fontFamily:FONT,marginBottom:20}}>
          Your estimate is empty. Here's how to get started:
        </div>
        {[
          {icon:"📍",label:"Set project info",desc:"Add address and building area",tab:"properties"},
          {icon:"🏗️",label:"Start estimating",desc:"Add quantities and costs by division",tab:"estimate"},
          {icon:"👥",label:"Add trade partners",desc:"Build your bid recipient list",tab:"properties"},
        ].map(({icon,label,desc,tab})=>(
          <div key={label} onClick={()=>setActiveTab(tab as any)}
            style={{display:"flex",alignItems:"center",gap:12,padding:"10px 12px",borderRadius:6,cursor:"pointer",marginBottom:6,background:T.bgApp,border:`1px solid ${T.borderPrimary}`,transition:"border-color 0.15s"}}
            onMouseEnter={e=>e.currentTarget.style.borderColor=T.borderAccent}
            onMouseLeave={e=>e.currentTarget.style.borderColor=T.borderPrimary}>
            <span style={{fontSize:20,flexShrink:0}}>{icon}</span>
            <div>
              <div style={{fontSize:12,fontWeight:600,color:T.textSecondary,fontFamily:FONT}}>{label}</div>
              <div style={{fontSize:11,color:T.textWeak,fontFamily:FONT}}>{desc}</div>
            </div>
            <span style={{marginLeft:"auto",color:T.borderAccent,fontSize:12,fontFamily:FONT}}>→</span>
          </div>
        ))}
      </div>
      <div style={{flexShrink:0,minWidth:180,maxWidth:220,display:"flex",flexDirection:"column" as any,justifyContent:"center",gap:10}}>
        <div style={{fontSize:11,fontWeight:700,color:T.textWeak,letterSpacing:".1em",textTransform:"uppercase",fontFamily:FONT,marginBottom:4}}>Quick Stats</div>
        {[
          {label:"Divisions Active",value:String(divisions.length),goto:null},
          {label:"Building Area",value:buildingArea>0?`${buildingArea.toLocaleString()} SF`:"Not set",goto:"properties"},
          {label:"Duration",value:duration>0?`${duration} Month${duration!==1?"s":""}`:"Not set",goto:"properties"},
        ].map(({label,value,goto})=>(
          <div key={label} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"7px 10px",background:T.bgApp,borderRadius:3,border:`1px solid ${T.borderPrimary}`}}>
            <span style={{fontSize:11,color:T.textWeak,fontFamily:FONT}}>{label}</span>
            <span style={{fontSize:11,fontWeight:600,color:T.textSecondary,fontFamily:FONT}}>
              {goto
                ?<EditableStat T={T} onClick={()=>onEditSetting(goto)} title={`Set ${label.toLowerCase()} on Project Info`}>{value}</EditableStat>
                :value}
            </span>
          </div>
        ))}
        <button onClick={()=>setActiveTab("estimate")}
          style={{marginTop:8,padding:"10px",background:T.borderAccent,color:"#fff",border:"none",borderRadius:6,fontFamily:FONT,fontSize:13,fontWeight:700,cursor:"pointer"}}>
          Start Estimating →
        </button>
      </div>
    </div>
  )}

  {/* ── KPI Cards ── */}
  {(()=>{
    const SK_S=["Hard Bid","Allowance","Needs Review","Other"];
    const SC_S={"Hard Bid":{fill:"#4abf6a",text:"#7ad49a",label:"Hard Bid"},"Allowance":{fill:"#4a8fbf",text:"#7ab8d4",label:"Allowance"},"Needs Review":{fill:"#bfb04a",text:"#d4c87a",label:"Needs Review"},"Other":{fill:"#666888",text:"#9999bb",label:"Other"}};
    const CW_S={"Hard Bid":1,"Allowance":.5,"Needs Review":0,"Other":.25};
    const allItemsS=divisions.flatMap(div=>div.subcategories.flatMap(sub=>[...walkItems(sub.rows)].map(item=>{
      const li=itemState[item.id]||{};
      if(isClarification(li.costStatus||""))return{total:0,status:"Other",proposal:li.proposal};
      const total=li.included!==false&&!li.altType?(li.qty||0)*(li.unitCost||0):0;
      const status=li.costStatus&&SK_S.includes(li.costStatus)?li.costStatus:"Other";
      return{total,status,proposal:li.proposal};
    }).filter(i=>i.total>0)));
    const bktsS=(items)=>{const b:Record<string,number>={};SK_S.forEach(k=>{b[k]=0;});items.forEach(i=>{b[i.status]=(b[i.status]||0)+i.total;});return{buckets:b,grandSum:Object.values(b).reduce((a,v)=>a+v,0)};};
    const{buckets:allBkt,grandSum:allSum}=bktsS(allItemsS);
    const os=(()=>{const{buckets:b,grandSum:gs}=bktsS(allItemsS);if(gs===0)return null;return Math.round((SK_S.reduce((a,k)=>a+(b[k]||0)*CW_S[k],0)/gs)*100);})();
    const sc=os===null?T.textMuted:os>=75?T.positive:os>=45?T.costStatus["Needs Review"].badgeText:T.negative;
    // mini donut
    const dSz=100;const dcx=dSz/2,dcy=dSz/2,dr=dSz/2-6,dri=dr*.52;
    let dAng=-Math.PI/2;const dSegs=[];
    SK_S.forEach(key=>{const val=allBkt[key]||0;if(!val||!allSum)return;const sw=(val/allSum)*2*Math.PI,ea=dAng+sw;
      const x1=dcx+dr*Math.cos(dAng),y1=dcy+dr*Math.sin(dAng),x2=dcx+dr*Math.cos(ea),y2=dcy+dr*Math.sin(ea);
      const ix1=dcx+dri*Math.cos(dAng),iy1=dcy+dri*Math.sin(dAng),ix2=dcx+dri*Math.cos(ea),iy2=dcy+dri*Math.sin(ea);
      const lg=sw>Math.PI?1:0;
      dSegs.push({key,fill:SC_S[key].fill,pct:val/allSum*100,d:`M${ix1},${iy1} L${x1},${y1} A${dr},${dr} 0 ${lg},1 ${x2},${y2} L${ix2},${iy2} A${dri},${dri} 0 ${lg},0 ${ix1},${iy1} Z`});
      dAng=ea;});
    return (
      <>
        {/* KPI row */}
        <div style={{display:"flex",gap:14,marginBottom:20,flexWrap:"wrap"}}>
          {[
            {label:"Total Estimate",value:fmt(final.total),sub:null,col:T.borderAccent,goto:null},
            {label:"Duration",value:duration>0?`${duration} Month${duration!==1?"s":""}`:"Not set",sub:null,col:duration>0?T.textSecondary:T.textWeak,goto:"properties"},
            ...(os!==null?[{label:"Confidence",value:`${os} / 100`,sub:os>=75?"High":os>=45?"Moderate":"Low",col:sc,goto:null}]:[]),
          ].map(({label,value,sub,col,goto})=>(
            <div key={label} style={{flex:1,minWidth:160,background:T.bgPanel,border:`1px solid ${T.borderPrimary}`,borderRadius:6,padding:"14px 18px"}}>
              <div style={{fontSize:10,color:T.textWeak,letterSpacing:".14em",textTransform:"uppercase",marginBottom:8}}>{label}</div>
              <div style={{fontSize:20,fontWeight:700,color:col,lineHeight:1.1}}>
                {goto
                  ?<EditableStat T={T} onClick={()=>onEditSetting(goto)} title="Set the duration on Project Info">{value}</EditableStat>
                  :value}
              </div>
              {sub&&<div style={{fontSize:10,color:T.textMuted,marginTop:5}}>{sub}</div>}
            </div>
          ))}
          {/* Cost per SF card — per-proposal breakdown */}
          {blendedSF>0&&(
            <div style={{flex:1,minWidth:160,background:T.bgPanel,border:`1px solid ${T.borderPrimary}`,borderRadius:6,padding:"14px 18px"}}>
              <div style={{fontSize:10,color:T.textWeak,letterSpacing:".14em",textTransform:"uppercase",marginBottom:8}}>Cost per SF</div>
              <div style={{fontSize:20,fontWeight:700,color:T.positive,lineHeight:1.1}}>{fmtSF(final.costPerSF)} / SF</div>
              {proposals.length>1?(
                <div style={{marginTop:6,display:"flex",flexDirection:"column",gap:3}}>
                  {proposals.map(p=>{
                    const pSF=proposalAreas[p.id]||0;
                    if(!pSF)return null;
                    const pCSF=((grandTotal.byProp[p.id]||0)+(contingencyByProp.byProp[p.id]||0))/pSF;
                    const pc=getPC(p.id,proposals);
                    return(
                      <div key={p.id} style={{display:"flex",alignItems:"center",gap:6}}>
                        <span style={{width:6,height:6,borderRadius:"50%",background:pc.text,flexShrink:0,display:"inline-block"}}/>
                        <span style={{fontSize:10,color:T.textMuted,flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{p.name}</span>
                        <span style={{fontSize:10,fontWeight:600,color:pc.text,flexShrink:0}}>{fmtSF(pCSF)}</span>
                        <span style={{fontSize:10,color:T.textWeak,flexShrink:0}}>{pSF.toLocaleString()} SF</span>
                      </div>
                    );
                  })}
                </div>
              ):(
                <div style={{fontSize:10,color:T.textMuted,marginTop:5}}>{blendedSF.toLocaleString()} SF</div>
              )}
            </div>
          )}
          {/* Conceptual Budget indicator card */}
          {budgetRange?(
            <div style={{flex:1,minWidth:160,background:T.bgPanel,border:`1px solid ${T.borderPrimary}`,borderRadius:6,padding:"14px 18px",position:"relative"}}>
              <div style={{fontSize:10,color:T.textWeak,letterSpacing:".14em",textTransform:"uppercase",marginBottom:8}}>Conceptual Budget</div>
              <div style={{fontSize:16,fontWeight:700,color:final.total>budgetRange.high?T.negative:final.total<budgetRange.low?"#4a8fbf":T.positive,lineHeight:1.1}}>
                {fmt(budgetRange.low)} – {fmt(budgetRange.high)}
              </div>
              <div style={{fontSize:10,color:T.textMuted,marginTop:5}}>
                {final.total>budgetRange.high?"Over Budget":final.total<budgetRange.low?"Under Budget":"Within Range"}
                {budgetRange.name?` · ${budgetRange.name}`:""}
              </div>
              <button onClick={()=>setShowBudgetPicker(true)} style={{position:"absolute",top:8,right:8,background:"none",border:"none",color:T.textWeak,fontSize:10,cursor:"pointer",opacity:.6}}><Icon.Edit size={12}/></button>
            </div>
          ):(
            <div onClick={()=>setShowBudgetPicker(true)} style={{flex:1,minWidth:160,background:T.bgPanel,border:`1px dashed ${T.borderSecondary}`,borderRadius:6,padding:"14px 18px",cursor:"pointer",opacity:.6,display:"flex",alignItems:"center",justifyContent:"center"}}>
              <span style={{fontSize:10,color:T.textWeak}}>+ Link Conceptual Budget</span>
            </div>
          )}
        </div>
        {/* Expandable Proposal Packages */}
        {proposals.length>0&&(
          <div style={{marginBottom:20}}>
            {/* How far down to open.
                Opening a package used to run straight to line items, because
                divisions and subcategories default to open and nothing set
                them otherwise -- so the first click on a package buried the
                thing you clicked it to see.

                These are ACTIONS, not a mode. Each writes the whole tree to
                that depth and the carets work as before afterwards. Marking
                one as "current" would start lying the moment a single caret
                was touched. */}
            <div style={{display:"flex",alignItems:"center",gap:4,marginBottom:10,flexWrap:"wrap" as any}}>
              <span style={{fontSize:10,color:T.textWeak,letterSpacing:".1em",textTransform:"uppercase" as any,marginRight:4}}>Show</span>
              {([["Packages",0],["Divisions",1],["Subcategories",2],["Line items",3]] as const).map(([label,depth])=>(
                <button key={label} onClick={()=>setTreeDepth(depth)}
                  title={depth===0?"Close every package":`Open every package down to ${label.toLowerCase()}`}
                  style={{fontSize:10,padding:"4px 10px",minHeight:24,borderRadius:3,cursor:"pointer",fontFamily:FONT,
                    border:`1px solid ${T.borderSecondary}`,background:"transparent",color:T.textWeak}}
                  onMouseEnter={e=>{e.currentTarget.style.borderColor=T.borderAccent;e.currentTarget.style.color=T.borderAccent;}}
                  onMouseLeave={e=>{e.currentTarget.style.borderColor=T.borderSecondary;e.currentTarget.style.color=T.textWeak;}}>
                  {label}
                </button>
              ))}
            </div>
            {proposals.map(prop=>{
              const pc=getPC(prop.id,proposals);
              const directTotal=grandTotal.byProp[prop.id]||0;
              const contForProp=contingencyByProp.byProp[prop.id]||0;
              const total=contSep?directTotal:directTotal+contForProp;
              const sf=sfFor(prop.id);
              const csf=sf>0&&total!==0?total/sf:0;
              const pct=grandTotal.subtotal!==0?(directTotal/Math.abs(grandTotal.subtotal))*100:0;
              const isExpanded=bdCollapsed[`pkg_${prop.id}`];
              // Build division → subcategory → (group | item) tree.
              // Preserves the Estimate tab's group structure so the
              // summary doesn't flatten items from different groups
              // (e.g. "Main House" in PROJECT MANAGER vs PROJECT
              // EXECUTIVE) into one ambiguous list.
              const buildItem=(item:any)=>{
                const li=itemState[item.id]||{};
                if(!li.included||li.proposal!==prop.id||getItemTotal(li,itemState)===0||li.altType)return null;
                return{type:"item" as const,id:item.id,desc:li.description||item.description,
                  direct:getItemTotal(li,itemState),cont:calcItemCont(li,contingency,itemState)};
              };
              const grouped=divisions.map(div=>{
                const subs=div.subcategories.map(sub=>{
                  const entries:any[]=[];
                  sub.rows.forEach((row:any)=>{
                    if(row.type==="group"){
                      const items=(row.children||[]).map(buildItem).filter(Boolean) as any[];
                      if(items.length===0)return;
                      const direct=items.reduce((a,i)=>a+i.direct,0);
                      const cont=items.reduce((a,i)=>a+i.cont,0);
                      entries.push({type:"group",id:row.id,name:row.name||"Group",items,direct,cont});
                    } else {
                      const built=buildItem(row);
                      if(built)entries.push(built);
                    }
                  });
                  if(entries.length===0)return null;
                  const subDirect=entries.reduce((a,e)=>a+e.direct,0);
                  const subCont=entries.reduce((a,e)=>a+e.cont,0);
                  return{sub,entries,subDirect,subCont};
                }).filter(Boolean) as any[];
                const divDirect=subs.reduce((a,s)=>a+s.subDirect,0);
                const divCont=subs.reduce((a,s)=>a+s.subCont,0);
                return{div,subs,divDirect,divCont};
              }).filter(d=>d.subs.length>0);
              return (
                <div key={prop.id} style={{background:T.bgPanel,borderRadius:6,marginBottom:10,borderLeft:`1px solid ${pc.text}28`,borderRight:`1px solid ${pc.text}28`,borderBottom:`1px solid ${pc.text}28`,borderTop:`1px solid ${pc.text}28`,overflow:"hidden"}}>
                  {/* Clickable header */}
                  <div onClick={()=>setBdCollapsed(p=>({...p,[`pkg_${prop.id}`]:!p[`pkg_${prop.id}`]}))}
                    style={{display:"flex",alignItems:"center",padding:"14px 18px",cursor:"pointer",userSelect:"none"}}
                    onMouseEnter={e=>e.currentTarget.style.background=T.bgHover}
                    onMouseLeave={e=>e.currentTarget.style.background=""}>
                    <span style={{fontSize:11,color:T.textWeak,marginRight:10,transition:"transform .15s",transform:isExpanded?"rotate(0deg)":"rotate(-90deg)"}}>▼</span>
                    <div style={{flex:1}}>
                      <div style={{fontSize:16,color:pc.text,fontWeight:700,letterSpacing:"-.005em"}}>{prop.name}</div>
                      <div style={{fontSize:11,color:T.textMuted,marginTop:3}}>{pct.toFixed(0)}% of total{csf>0?` · ${fmtSF(csf)}/sf`:""}</div>
                    </div>
                    <div style={{textAlign:"right"}}>
                      <div style={{fontSize:16,fontWeight:700,color:total<0?T.negative:pc.text,letterSpacing:"-.005em"}}>{fmt(total)}</div>
                      {contSep&&contForProp!==0&&<div style={{fontSize:11,color:`${pc.text}88`,fontStyle:"italic"}}>+{fmt(contForProp)} cont.</div>}
                    </div>
                  </div>
                  {/* Progress bar */}
                  {/* The only coloured bar on the card, so it can only mean
                      one thing. There used to be a 3px rule in the same
                      colour across the top: no information, heavier than the
                      measurement below it, and full width -- which read as
                      the same bar at 100%. The card's identity is already in
                      the title, the total and the legend dot; the colour here
                      is doing work, so this is the mark that gets the weight.
                      The track is visible enough that 85% reads as a
                      proportion rather than as a bar that happens to stop. */}
                  <div style={{height:4,background:`${pc.text}1f`}}>
                    <div style={{width:`${Math.min(100,Math.abs(pct))}%`,height:"100%",background:pc.bar}}/>
                  </div>
                  {/* Expandable detail tree */}
                  {isExpanded&&(
                    <div style={{padding:"8px 18px 16px"}}>
                      {contSep&&(
                        <div style={{display:"flex",alignItems:"center",padding:"2px 8px 4px",marginBottom:2}}>
                          <span style={{flex:1}}/>
                          <span style={{fontSize:10,color:T.textWeak,letterSpacing:".1em",textTransform:"uppercase",minWidth:72,textAlign:"right"}}>Direct</span>
                          <span style={{fontSize:10,color:T.textWeak,letterSpacing:".1em",textTransform:"uppercase",minWidth:66,textAlign:"right",fontStyle:"italic"}}>Cont.</span>
                          {sf>0&&<span style={{fontSize:10,color:T.textWeak,letterSpacing:".1em",textTransform:"uppercase",minWidth:58,textAlign:"right"}}>$/SF</span>}
                        </div>
                      )}
                      {grouped.length>0?grouped.map(({div,subs,divDirect,divCont})=>{
                        const sep=contSep;
                        const divDisplay=sep?divDirect:divDirect+divCont;
                        const bdKey=`${prop.id}_${div.id}`;
                        const isDivCollapsed=bdCollapsed[bdKey];
                        return(
                          <div key={div.id} style={{marginBottom:4}}>
                            <div onClick={()=>setBdCollapsed(p=>({...p,[bdKey]:!p[bdKey]}))}
                              style={{display:"flex",alignItems:"center",padding:"9px 10px",background:T.bgAccentFaint,borderRadius:3,borderLeft:`3px solid ${pc.bar}`,marginBottom:2,cursor:"pointer",userSelect:"none"}}
                              onMouseEnter={e=>e.currentTarget.style.background=T.bgHover}
                              onMouseLeave={e=>e.currentTarget.style.background=T.bgAccentFaint}>
                              <span style={{fontSize:11,color:T.textWeak,marginRight:8,transition:"transform .15s",transform:isDivCollapsed?"rotate(-90deg)":"rotate(0deg)"}}>▼</span>
                              <span style={{flex:1,fontSize:14,fontWeight:700,color:T.textSecondary,letterSpacing:".02em"}}>{div.code} · {div.name}</span>
                              <span style={{fontSize:14,fontWeight:700,color:divDisplay<0?T.negative:T.textSecondary,minWidth:84,textAlign:"right"}}>{fmt(divDisplay)}</span>
                              {sep&&<span style={{fontSize:10,fontWeight:500,color:divCont!==0?`${pc.bar}88`:T.borderSecondary,fontStyle:"italic",minWidth:66,textAlign:"right"}}>{divCont!==0?fmt(divCont):"--"}</span>}
                              {sf>0&&<span style={{fontSize:10,color:T.textMuted,minWidth:58,textAlign:"right"}}>{divDisplay!==0?fmtSF(divDisplay/sf):""}</span>}
                            </div>
                            {!isDivCollapsed&&subs.map(({sub,entries,subDirect,subCont}:any)=>{
                              const subDisplay=sep?subDirect:subDirect+subCont;
                              const subKey=`sub_${prop.id}_${div.id}_${sub.id}`;
                              const isSubCollapsed=bdCollapsed[subKey]; // default open (undefined = false)
                              return(
                                <div key={sub.id} style={{marginBottom:6,paddingLeft:12}}>
                                  <div onClick={()=>setBdCollapsed(p=>({...p,[subKey]:!p[subKey]}))}
                                    style={{display:"flex",alignItems:"center",padding:"7px 8px",borderTop:`1px solid ${T.borderPrimary}`,borderBottom:`1px solid ${T.borderPrimary}`,background:T.bgAccentFaint,cursor:"pointer",userSelect:"none",marginTop:4}}
                                    onMouseEnter={e=>e.currentTarget.style.background=T.bgHover}
                                    onMouseLeave={e=>e.currentTarget.style.background=T.bgAccentFaint}>
                                    <span style={{fontSize:10,color:T.textWeak,marginRight:6,transition:"transform .15s",transform:isSubCollapsed?"rotate(-90deg)":"rotate(0deg)"}}>▼</span>
                                    <span style={{flex:1,fontSize:12,color:T.textSecondary,fontWeight:700,letterSpacing:".06em",textTransform:"uppercase"}}>{sub.costCode&&<span style={{color:T.textWeak,marginRight:6,fontWeight:600}}>{sub.costCode}</span>}{sub.name}</span>
                                    <span style={{fontSize:12,fontWeight:600,color:subDisplay<0?T.negative:T.textSecondary,minWidth:72,textAlign:"right"}}>{fmt(subDisplay)}</span>
                                    {sep&&<span style={{fontSize:10,color:subCont!==0?`${pc.bar}77`:T.borderSecondary,fontStyle:"italic",minWidth:66,textAlign:"right"}}>{subCont!==0?fmt(subCont):"--"}</span>}
                                    {sf>0&&<span style={{fontSize:10,color:T.textMuted,minWidth:58,textAlign:"right"}}>{subDisplay!==0?fmtSF(subDisplay/sf):""}</span>}
                                  </div>
                                  {!isSubCollapsed&&entries.map((entry:any,ei:number)=>{
                                    if(entry.type==="group"){
                                      // Show only the group's roll-up — its contents are
                                      // already summarised by the total, so listing each
                                      // child underneath would duplicate information.
                                      // Render like a regular item row but bold to signal
                                      // it's a roll-up. Click navigates to the first item.
                                      const groupDisplay=sep?entry.direct:entry.direct+entry.cont;
                                      const firstItemId=entry.items[0]?.id;
                                      return(
                                        <div key={entry.id}
                                          onClick={firstItemId?()=>navigateToItem(firstItemId):undefined}
                                          style={{display:"flex",alignItems:"center",padding:"4px 8px 4px 16px",borderBottom:`1px solid ${T.borderPrimary}20`,cursor:firstItemId?"pointer":"default"}}
                                          onMouseEnter={e=>e.currentTarget.style.background=T.bgHover}
                                          onMouseLeave={e=>e.currentTarget.style.background=""}>
                                          <span style={{flex:1,fontSize:11,color:T.textSecondary,fontWeight:700,marginRight:8,textTransform:"uppercase" as any}}>{entry.name}</span>
                                          <span style={{fontSize:11,fontWeight:700,color:groupDisplay<0?T.negative:T.positive,flexShrink:0,minWidth:72,textAlign:"right"}}>{fmt(groupDisplay)}</span>
                                          {sep&&<span style={{fontSize:10,color:entry.cont!==0?`${pc.bar}77`:T.borderSecondary,fontStyle:"italic",flexShrink:0,minWidth:66,textAlign:"right"}}>{entry.cont!==0?fmt(entry.cont):"--"}</span>}
                                          {sf>0&&<span style={{fontSize:10,color:T.textMuted,flexShrink:0,minWidth:58,textAlign:"right"}}>{groupDisplay!==0?fmtSF(groupDisplay/sf):""}</span>}
                                        </div>
                                      );
                                    }
                                    // Standalone (ungrouped) item
                                    const itemDisplay=sep?entry.direct:entry.direct+entry.cont;
                                    return(
                                      <div key={`it-${ei}`} onClick={()=>navigateToItem(entry.id)}
                                        style={{display:"flex",alignItems:"center",padding:"4px 8px 4px 16px",borderBottom:`1px solid ${T.borderPrimary}20`,cursor:"pointer"}}
                                        onMouseEnter={e=>e.currentTarget.style.background=T.bgHover}
                                        onMouseLeave={e=>e.currentTarget.style.background=""}>
                                        <span style={{flex:1,fontSize:11,color:T.textSecondary,marginRight:8}}>{entry.desc}</span>
                                        <span style={{fontSize:11,color:itemDisplay<0?T.negative:T.positive,fontWeight:500,flexShrink:0,minWidth:72,textAlign:"right"}}>{fmt(itemDisplay)}</span>
                                        {sep&&<span style={{fontSize:10,color:entry.cont!==0?`${pc.bar}77`:T.borderSecondary,fontStyle:"italic",flexShrink:0,minWidth:66,textAlign:"right"}}>{entry.cont!==0?fmt(entry.cont):"--"}</span>}
                                        {sf>0&&<span style={{fontSize:10,color:T.textMuted,flexShrink:0,minWidth:58,textAlign:"right"}}>{itemDisplay!==0?fmtSF(itemDisplay/sf):""}</span>}
                                      </div>
                                    );
                                  })}
                                </div>
                              );
                            })}
                          </div>
                        );
                      }):<div style={{fontSize:11,color:T.textWeak,fontStyle:"italic",padding:"8px 0"}}>No included line items with cost assigned.</div>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </>
    );
  })()}

  {/* ── Allowance CTA banner ── */}
  {(()=>{
    const allowanceItems=divisions.flatMap(div=>div.subcategories.flatMap(sub=>[...walkItems(sub.rows)].filter(item=>{
      const li=itemState[item.id]||{};
      return li.included!==false&&!li.altType&&li.costStatus==="Allowance";
    })));
    const allowanceTotal=allowanceItems.reduce((sum,item)=>{const li=itemState[item.id]||{};return sum+(li.qty||0)*(li.unitCost||0);},0);
    if(allowanceItems.length===0)return null;
    return (
      <div
        onClick={()=>setShowAllowancePanel(true)}
        style={{
          display:"flex",alignItems:"center",justifyContent:"space-between",
          background:`#4a8fbf12`,border:`1px solid #4a8fbf44`,
          borderRadius:6,padding:"12px 18px",marginBottom:20,cursor:"pointer",transition:"background 0.15s",
        }}
        onMouseEnter={e=>(e.currentTarget.style.background="#4a8fbf22")}
        onMouseLeave={e=>(e.currentTarget.style.background="#4a8fbf12")}
      >
        <div>
          <div style={{fontSize:12,fontWeight:700,color:"#5aaad4",fontFamily:FONT}}>
            {allowanceItems.length} Allowance item{allowanceItems.length!==1?"s":""} — {fmt(allowanceTotal)} to price out
          </div>
          <div style={{fontSize:11,color:T.textWeak,marginTop:2,fontFamily:FONT}}>Convert allowances to hard bids as pricing comes in to improve estimate confidence.</div>
        </div>
        <div style={{fontSize:13,fontWeight:700,color:"#5aaad4",fontFamily:FONT,flexShrink:0,marginLeft:16}}>Review Allowances →</div>
      </div>
    );
  })()}

  {/* The switch sits on the thing it switches. Up beside the page title it
      was a page-level setting four hundred pixels from the only columns it
      changes, so it read as decoration and went unused. */}
  {contPerLine&&(
    <div style={{display:"flex",alignItems:"center",justifyContent:"flex-end",gap:6,marginBottom:6}}>
      <span style={{fontSize:10,color:T.textWeak,letterSpacing:".08em",textTransform:"uppercase"}}>Contingency:</span>
      <button onClick={()=>setContingencyDisplay("included")}
        title="Roll each line's contingency into its division total"
        style={{fontSize:10,padding:"4px 10px",minHeight:24,borderRadius:3,border:`1px solid ${T.borderSecondary}`,cursor:"pointer",fontFamily:FONT,
          background:!contSep?T.bgAccentLight:"transparent",
          color:!contSep?T.borderAccent:T.textWeak,
          fontWeight:!contSep?600:400}}>
        Included
      </button>
      <button onClick={()=>setContingencyDisplay("separate")}
        title="Show contingency in its own column beside each package"
        style={{fontSize:10,padding:"4px 10px",minHeight:24,borderRadius:3,border:`1px solid ${T.borderSecondary}`,cursor:"pointer",fontFamily:FONT,
          background:contSep?T.bgAccentLight:"transparent",
          color:contSep?T.borderAccent:T.textWeak,
          fontWeight:contSep?600:400}}>
        Separate
      </button>
    </div>
  )}

  <table style={{width:"100%",borderCollapse:"collapse",marginBottom:34}}>
    <thead>
      <tr style={{borderBottom:`2px solid ${T.borderAccentFaint}`}}>
        <th style={{textAlign:"left",fontSize:10,color:T.textWeak,letterSpacing:".12em",padding:"8px 10px",fontWeight:500,textTransform:"uppercase"}}>Division</th>
        {proposals.map(p=>{
          const pc=getPC(p.id,proposals);
          return contSep?(
            <React.Fragment key={p.id}>
              <th style={{textAlign:"right",fontSize:10,color:pc.text,letterSpacing:".1em",padding:"8px 10px 8px 6px",fontWeight:500,textTransform:"uppercase"}}>{p.name}</th>
              <th style={{textAlign:"right",fontSize:10,color:pc.text+"66",letterSpacing:".1em",padding:"8px 4px 8px 2px",fontWeight:500,textTransform:"uppercase"}}>Cont.</th>
            </React.Fragment>
          ):(
            <th key={p.id} style={{textAlign:"right",fontSize:10,color:pc.text,letterSpacing:".1em",padding:"8px 10px",fontWeight:500,textTransform:"uppercase"}}>{p.name}</th>
          );
        })}
        <th style={{textAlign:"right",fontSize:10,color:T.textWeak,letterSpacing:".12em",padding:"8px 10px",fontWeight:500,textTransform:"uppercase"}}>Div Total</th>
        <th style={{textAlign:"right",fontSize:10,color:T.textWeak,letterSpacing:".12em",padding:"8px 10px",fontWeight:500,textTransform:"uppercase"}}>$/SF</th>
      </tr>
    </thead>
    <tbody>
      {divisions.map(div=>{
        const dt=divTotals[div.id];
        const divContMap=contingencyByProp.byDivByProp[div.id]||{};
        const divContTotal=Object.values(divContMap).reduce((a:number,v)=>a+(v as number),0);
        const displayTotal=contSep?dt.total:dt.total+divContTotal;
        const dSF=displayTotal!==0?Object.entries(dt.byProp).reduce((a:number,[p,v])=>a+(sfFor(p)*Math.abs(v as number)),0)/Math.abs(dt.total||1):buildingArea;
        const csf=dSF>0&&displayTotal!==0?displayTotal/dSF:0;
        return (
          <tr key={div.id} style={{borderBottom:`1px solid ${T.bgHeader}`,cursor:"pointer"}} onClick={()=>{setActiveTab("estimate");setActiveDiv(div.id);}}>
            <td style={{padding:"8px 10px",fontSize:12}}><span style={{color:T.textWeak,marginRight:8,fontSize:10}}>{div.code}</span><span style={{color:T.textSecondary}}>{div.name}</span></td>
            {proposals.map(p=>{
              const a=dt.byProp[p.id]||0,sf=sfFor(p.id);
              if(contSep){
                const co=divContMap[p.id]||0,pc=getPC(p.id,proposals);
                return (
                  <React.Fragment key={p.id}>
                    <td style={{padding:"8px 6px 8px 10px",textAlign:"right",fontSize:12,color:a<0?T.negative:a!==0?pc.text:T.borderSecondary}}>
                      {a!==0?fmt(a):"--"}
                    </td>
                    <td style={{padding:"8px 4px 8px 2px",textAlign:"right",fontSize:11,color:co<0?T.negative:co!==0?pc.text+"77":T.borderSecondary,fontStyle:"italic"}}>
                      {co!==0?fmt(co):"--"}
                    </td>
                  </React.Fragment>
                );
              }
              const combined=a+(divContMap[p.id]||0),csfP=sf>0&&combined!==0?combined/sf:0;
              return <td key={p.id} style={{padding:"8px 10px",textAlign:"right",fontSize:12,color:combined<0?T.negative:combined!==0?getPC(p.id,proposals).text:T.borderSecondary}}>
                {combined!==0?fmt(combined):"--"}
              </td>;
            })}
            <td style={{padding:"8px 10px",textAlign:"right",fontSize:13,fontWeight:500,color:displayTotal<0?T.negative:displayTotal>0?T.positive:T.borderSecondary}}>{displayTotal!==0?fmt(displayTotal):"--"}</td>
            <td style={{padding:"8px 10px",textAlign:"right",fontSize:11,color:T.textMuted}}>{csf!==0?fmtSF(csf):"--"}</td>
          </tr>
        );
      })}
    </tbody>
    <tfoot>
      <tr style={{borderTop:`2px solid ${T.borderAccentFaint}`}}>
        <td style={{padding:"10px",fontSize:11,color:T.textMuted,letterSpacing:".08em",textTransform:"uppercase"}}>
          {contSep?"Direct Cost Subtotal":"Subtotal"}
        </td>
        {proposals.map(p=>{
          const a=grandTotal.byProp[p.id]||0,sf=sfFor(p.id),pc=getPC(p.id,proposals);
          if(contSep){
            const co=contingencyByProp.byProp[p.id]||0;
            return (
              <React.Fragment key={p.id}>
                <td style={{padding:"10px 6px 10px 10px",textAlign:"right",fontSize:13,fontWeight:600,color:a<0?T.negative:a!==0?pc.text:T.borderSecondary}}>
                  {a!==0?fmt(a):"--"}
                  {a!==0&&sf>0&&<div style={{fontSize:10,color:T.textMuted,marginTop:1}}>{fmtSF(a/sf)}/sf</div>}
                </td>
                <td style={{padding:"10px 4px 10px 2px",textAlign:"right",fontSize:12,fontWeight:600,color:co<0?T.negative:co!==0?pc.text+"77":T.borderSecondary,fontStyle:"italic"}}>
                  {co!==0?fmt(co):"--"}
                </td>
              </React.Fragment>
            );
          }
          const combined=a+(contingencyByProp.byProp[p.id]||0),csf=sf>0&&combined!==0?combined/sf:0;
          return <td key={p.id} style={{padding:"10px",textAlign:"right",fontSize:13,fontWeight:600,color:combined<0?T.negative:combined!==0?pc.text:T.borderSecondary}}>
            {combined!==0?fmt(combined):"--"}
            {combined!==0&&csf!==0&&<div style={{fontSize:10,color:T.textMuted,marginTop:1}}>{fmtSF(csf)}/sf</div>}
          </td>;
        })}
        {contSep?(
          <>
            <td style={{padding:"10px",textAlign:"right",fontSize:14,fontWeight:700,color:grandTotal.subtotal<0?T.negative:T.borderAccent}}>{fmt(grandTotal.subtotal)}</td>
            <td style={{padding:"10px",textAlign:"right",fontSize:11,color:T.textMuted}}>{buildingArea>0?fmtSF(grandTotal.subtotal/buildingArea):"--"}</td>
          </>
        ):(
          <>
            <td style={{padding:"10px",textAlign:"right",fontSize:14,fontWeight:700,color:(grandTotal.subtotal+contingencyByProp.total)<0?T.negative:T.borderAccent}}>{fmt(grandTotal.subtotal+contingencyByProp.total)}</td>
            <td style={{padding:"10px",textAlign:"right",fontSize:11,color:T.textMuted}}>{buildingArea>0?fmtSF((grandTotal.subtotal+contingencyByProp.total)/buildingArea):"--"}</td>
          </>
        )}
      </tr>
    </tfoot>
  </table>
  <div style={{background:T.bgPanel,border:`1px solid ${T.borderPrimary}`,borderRadius:3,padding:"20px 26px",maxWidth:440,marginLeft:"auto"}}>
    {(()=>{
      // A row whose label carries a percentage is a row somebody chose, and the
      // first thing anyone does on seeing a total is want to try a different
      // one. The flag marks the rows that lead to where that choice is made.
      const mkLines:(readonly[string,number,string,boolean?])[]=[];
      // The two halves are named separately because they are different
      // promises. A line buffer belongs to a division and shows up in its
      // column; a project reserve belongs to the project and deliberately
      // does not, so calling the reserve "by line" put a label on it that
      // the columns above flatly contradicted.
      const lineCont=final.contingencyLine??final.contingency;
      const reserve=final.contingencyReserve??0;
      const resLabel=reserveLabel(contingencyPolicy);
      // Escalation sits between the cost of work and the markup on it,
      // because that is where it happens: fee is charged on the escalated
      // cost. Omitting the line would leave the sheet unable to get from
      // direct cost to the total.
      // `namesBase` is true in the presentation that folds line buffers into
      // direct cost. Escalation is a percentage of the subtotal BEFORE those
      // buffers -- it has to be, or switching this toggle would change the
      // project total -- so in that presentation the figure above is not the
      // base, and a bare "4.00%" would be a percentage of a number that is
      // not on the sheet. Name the base instead of inviting the check and
      // failing it.
      const escLine=(namesBase:boolean)=>{
        if((final.escalation??0)===0)return;
        const pct=(final.escalationPct??0).toFixed(2);
        mkLines.push([
          namesBase?`Escalation (${pct}% of ${fmt(final.subtotal)} direct cost)`:`Escalation (${pct}%)`,
          final.escalation,T.textMuted,
        ] as const);
      };
      const markupLines=()=>{
        if(markupMode==="combined"){mkLines.push([`OH&P (${overhead}%)`,final.overhead,T.textMuted,true] as const);return;}
        if(markupMode!=="profit")mkLines.push([`Overhead (${overhead}%)`,final.overhead,T.textMuted,true] as const);
        if(markupMode!=="overhead")mkLines.push([`Profit (${profit}%)`,final.profit,T.textMuted,true] as const);
      };
      if(contSep){
        mkLines.push(["Subtotal - Direct Costs",final.subtotal,T.textMuted] as const);
        escLine(false);  // the base is the line directly above
        markupLines();
        mkLines.push(["Before Contingency",final.beforeCont,T.textSecondary] as const);
        if(lineCont!==0)mkLines.push(["Contingency (by line)",lineCont,`${T.borderAccent}80`,true] as const);
        if(reserve!==0)mkLines.push([resLabel,reserve,`${T.borderAccent}80`,true] as const);
      } else {
        // "Included" folds the line buffers into direct cost, which is what
        // including them means. A reserve cannot be folded in -- no division
        // carries it -- so it stays on its own line in both presentations.
        mkLines.push(["Direct Costs",final.subtotal+lineCont,T.textMuted] as const);
        escLine(lineCont!==0);  // buffers are folded in above; name the real base
        markupLines();
        if(reserve!==0)mkLines.push([resLabel,reserve,`${T.borderAccent}80`,true] as const);
      }
      return mkLines.map(([lbl,val,col,editable])=>(
        <div key={lbl} style={{display:"flex",justifyContent:"space-between",padding:"6px 0",borderBottom:`1px solid ${T.bgInput}`,fontSize:12}}>
          <span style={{color:T.textTertiary}}>
            {editable
              ?<EditableStat T={T} onClick={()=>onEditSetting("properties-defaults")}
                 title="Change this on Project Defaults">{lbl}</EditableStat>
              :lbl}
          </span>
          <span style={{color:val<0?T.negative:col}}>{fmt(val)}</span>
        </div>
      ));
    })(
    )}
    {markupableSubtotal<grandTotal.subtotal&&(
      <div style={{display:"flex",alignItems:"center",gap:6,padding:"6px 0",borderBottom:`1px solid ${T.bgInput}`,fontSize:10,color:T.textMuted}}>
        <span style={{fontSize:10,padding:"1px 4px",borderRadius:3,background:"#c08040",color:"#fff",fontWeight:700,letterSpacing:.5,flexShrink:0}}>NO MU</span>
        <span>{fmt(grandTotal.subtotal-markupableSubtotal)} in direct costs excluded from OH&amp;P markup</span>
      </div>
    )}
    <div style={{display:"flex",justifyContent:"space-between",padding:"13px 0 4px",borderTop:`2px solid ${T.borderAccentFaint}`,marginTop:8}}>
      <span style={{fontSize:13,color:T.borderAccent,letterSpacing:".06em",textTransform:"uppercase"}}>Total Estimate</span>
      <span style={{fontSize:20,color:final.total<0?T.negative:T.borderAccent,fontWeight:700}}>{fmt(final.total)}</span>
    </div>
    <div style={{display:"flex",justifyContent:"space-between",paddingTop:5}}>
      <span style={{fontSize:11,color:T.textWeak}}>Cost per SF (blended)</span>
      <span style={{fontSize:13,color:T.positive}}>{fmtSF(final.costPerSF)} / SF</span>
    </div>
  </div>
  {/* ── Clarifications Panel ── */}
  {(()=>{
    const clarItems:{divName:string;subName:string;desc:string;notes:string;status:"By Others"|"Excluded"}[]=[];
    divisions.forEach(div=>{
      div.subcategories.forEach(sub=>{
        [...walkItems(sub.rows)].forEach(item=>{
          const li=itemState[item.id]||{};
          if(li.included&&(li.costStatus==="By Others"||li.costStatus==="Excluded")){
            clarItems.push({divName:`${div.code} · ${div.name}`,subName:sub.name,desc:li.description||item.description||"",notes:li.notes||"",status:li.costStatus as "By Others"|"Excluded"});
          }
        });
      });
    });
    if(clarItems.length===0)return null;
    const byOthers=clarItems.filter(i=>i.status==="By Others");
    const excluded=clarItems.filter(i=>i.status==="Excluded");
    const sections:[string,"By Others"|"Excluded",typeof byOthers,{rowBg:string;leftBar:string;badgeBg:string;badgeText:string}][]=[
      ["By Others","By Others",byOthers,T.costStatus["By Others"]],
      ["Excluded","Excluded",excluded,T.costStatus["Excluded"]],
    ].filter(([,,arr])=>(arr as any[]).length>0) as any;
    return (
      <div style={{marginTop:28}}>
        <div style={{fontSize:10,color:T.textWeak,letterSpacing:".12em",textTransform:"uppercase",marginBottom:14,paddingBottom:8,borderBottom:`1px solid ${T.borderPrimary}`,display:"flex",alignItems:"baseline",gap:8,flexWrap:"wrap" as const}}>
          <span>Excluded in This Estimate</span>
          <span style={{fontSize:10,color:T.textMuted,fontWeight:400,textTransform:"none",letterSpacing:0}}>
            — {clarItems.length} line item{clarItems.length!==1?"s":""} you priced or reviewed and are not carrying. Each traces back to a division, and none of them are in any total.
          </span>
        </div>
        <div style={{display:"flex",gap:18,flexWrap:"wrap"}}>
          {sections.map(([label,status,items,cs])=>(
            <div key={label} style={{flex:1,minWidth:260,background:T.bgPanel,border:`1px solid ${T.borderPrimary}`,borderTop:`3px solid ${cs.leftBar}`,borderRadius:6,overflow:"hidden"}}>
              <div style={{padding:"12px 16px 10px",display:"flex",alignItems:"center",justifyContent:"space-between",borderBottom:`1px solid ${T.borderPrimary}`}}>
                <span style={{fontSize:11,fontWeight:700,color:cs.badgeText,letterSpacing:".06em",textTransform:"uppercase"}}>{label}</span>
                <span style={{fontSize:10,background:cs.badgeBg,color:cs.badgeText,border:`1px solid ${cs.leftBar}44`,borderRadius:10,padding:"1px 8px",fontWeight:600}}>{items.length} item{items.length!==1?"s":""}</span>
              </div>
              <div style={{padding:"8px 0"}}>
                {items.map((item,idx)=>(
                  <div key={idx} style={{padding:"6px 16px",borderBottom:idx<items.length-1?`1px solid ${T.bgApp}`:undefined}}>
                    <div style={{fontSize:10,color:T.textWeak,letterSpacing:".08em",marginBottom:2}}>{item.divName}</div>
                    <div style={{fontSize:12,color:T.textSecondary,lineHeight:1.4}}>{item.desc||<span style={{fontStyle:"italic",color:T.textWeak}}>No description</span>}</div>
                    {item.notes&&<div style={{fontSize:10,color:T.textMuted,marginTop:2,fontStyle:"italic"}}>{item.notes}</div>}
                  </div>
                ))}
              </div>
              <div style={{padding:"8px 16px 10px",borderTop:`1px solid ${T.borderPrimary}`,background:T.bgApp}}>
                <div style={{fontSize:10,color:T.textWeak,fontStyle:"italic",lineHeight:1.5}}>
                  {status==="By Others"
                    ?"These items are acknowledged in scope but provided by other parties."
                    :"These items are explicitly excluded — no party is budgeting them."}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  })()}

  {/* ── Project Exclusions ── */}
  <div style={{marginTop:28}}>
    <div style={{fontSize:10,color:T.textWeak,letterSpacing:".12em",textTransform:"uppercase",marginBottom:14,paddingBottom:8,borderBottom:`1px solid ${T.borderPrimary}`,display:"flex",alignItems:"baseline",gap:8,flexWrap:"wrap" as const}}>
      <span>General Exclusions</span>
      <span style={{fontSize:10,color:T.textMuted,fontWeight:400,textTransform:"none",letterSpacing:0}}>
        — Standing proposal language, not tied to any line item. Nothing here was priced; it says what the estimate never covers. Appears in the Scope of Work.
      </span>
    </div>
    {/* Active exclusions — numbered table rows.
        Tagged by provenance: "organisation-wide or general" was exactly the
        distinction that was impossible to see from this list. */}
    {projectExclusions.length===0
      ? <div style={{fontSize:11,color:T.textMuted,fontStyle:"italic",marginBottom:12}}>No exclusions listed yet.</div>
      : (
        <div style={{border:`1px solid ${T.borderPrimary}`,borderRadius:3,overflow:"hidden",marginBottom:14}}>
          {projectExclusions.map((ex,i)=>(
            <div key={i} style={{display:"flex",alignItems:"center",gap:10,
              padding:"6px 10px",
              borderBottom:i<projectExclusions.length-1?`1px solid ${T.borderPrimary}`:"none",
              background:i%2===0?T.bgPanel:T.bgApp}}>
              <span style={{fontSize:10,color:T.textMuted,flexShrink:0,width:20,textAlign:"right",userSelect:"none"}}>{i+1}.</span>
              <span style={{flex:1,fontSize:11,color:T.textSecondary,lineHeight:1.4}}>{ex}</span>
              {(()=>{
                const fromLibrary=orgExclusions.some((o:any)=>o.text===ex);
                return (
                  <span title={fromLibrary
                    ?"From your organisation's standard library — applies to every project"
                    :"Written for this project only"}
                    style={{fontSize:10,flexShrink:0,padding:"1px 7px",borderRadius:10,fontWeight:600,letterSpacing:".04em",
                      background:fromLibrary?T.bgAccentFaint:"transparent",
                      color:fromLibrary?T.borderAccent:T.textWeak,
                      border:`1px solid ${fromLibrary?`${T.borderAccent}44`:T.borderPrimary}`}}>
                    {fromLibrary?"Standard":"This project"}
                  </span>
                );
              })()}
              <button onClick={()=>setProjectExclusions(p=>p.filter((_,j)=>j!==i))}
                style={{background:"none",border:"none",color:T.textWeak,cursor:"pointer",fontSize:14,lineHeight:1,padding:"0 3px",opacity:.45,flexShrink:0}}
                onMouseEnter={e=>(e.currentTarget.style.opacity="1")}
                onMouseLeave={e=>(e.currentTarget.style.opacity=".45")}>&times;</button>
            </div>
          ))}
        </div>
      )
    }
    {/* Add from org library — compact clickable list */}
    {(()=>{
      const available=orgExclusions.filter(o=>!projectExclusions.includes(o.text));
      if(available.length===0)return null;
      return(
        <div style={{marginBottom:10}}>
          <div style={{fontSize:10,color:T.textWeak,letterSpacing:".1em",textTransform:"uppercase",marginBottom:6}}>Standard library — click to add</div>
          <div style={{border:`1px solid ${T.borderPrimary}`,borderRadius:3,overflow:"hidden"}}>
            {available.map((o,i)=>(
              <div key={o.id}
                onClick={()=>setProjectExclusions(p=>[...p,o.text])}
                style={{display:"flex",alignItems:"center",gap:8,padding:"6px 10px",
                  borderBottom:i<available.length-1?`1px solid ${T.borderPrimary}`:"none",
                  background:T.bgApp,cursor:"pointer",transition:"background .1s"}}
                onMouseEnter={e=>(e.currentTarget.style.background=T.bgHover)}
                onMouseLeave={e=>(e.currentTarget.style.background=T.bgApp)}>
                <span style={{fontSize:11,color:T.borderAccent,flexShrink:0,lineHeight:1}}>+</span>
                <span style={{flex:1,fontSize:11,color:T.textTertiary,lineHeight:1.3}}>{o.text}</span>
              </div>
            ))}
          </div>
        </div>
      );
    })()}
    {/* Custom */}
    <div style={{display:"flex",gap:6,alignItems:"center",marginTop:10}}>
      <input className="ghost-input" value={newExclusionText} onChange={e=>setNewExclusionText(e.target.value)}
        placeholder="Type a custom exclusion…"
        onKeyDown={e=>{if(e.key==="Enter"&&newExclusionText.trim()){setProjectExclusions(p=>[...p,newExclusionText.trim()]);setNewExclusionText("");}}}
        style={{flex:1,fontSize:11,padding:"7px 10px",border:`1px solid ${T.borderSecondary}`,borderRadius:3,background:T.bgInput,color:T.textSecondary,fontFamily:FONT}}/>
      <button className="add-row-btn" onClick={()=>{if(newExclusionText.trim()){setProjectExclusions(p=>[...p,newExclusionText.trim()]);setNewExclusionText("");}}}
        style={{flexShrink:0,minHeight:24,whiteSpace:"nowrap" as any}}>+ Add</button>
    </div>
    <div style={{marginTop:10,fontSize:10,color:T.textMuted}}>
      Manage your standard library in <button onClick={()=>setActiveTab("settings-team")} style={{background:"none",border:"none",color:T.borderAccent,cursor:"pointer",fontFamily:FONT,fontSize:10,padding:0,textDecoration:"underline"}}>Organization Settings</button>.
    </div>
  </div>
</div>
);
}
